import clsx from 'clsx'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import Button from '~/src/components/Button'
import FadeImage from '~/src/components/FadeImage'
import ModalDialog from '~/src/components/ModalDialog'

interface Props {
  images: string[] | undefined
  onClickCallback?: (img: string | undefined, closeModal: () => void) => void
}

// Memoized fade image component to prevent re-renders during loading
const MemoizedFadeImage = memo(function MemoizedFadeImage({
  src,
  className,
  ...props
}: React.ComponentProps<typeof FadeImage>) {
  return <FadeImage src={src} className={className} {...props} />
})

// Memoized image button component
const ImageButton = memo(function ImageButton({
  img,
  focusedImage,
  onClick,
}: {
  img: string
  focusedImage: string | undefined
  onClick: (img: string) => void
}) {
  const handleClick = useCallback(() => {
    onClick(img)
  }, [img, onClick])

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
  }, [])

  if (typeof img !== 'string') return null

  const isSelected = img === focusedImage

  return (
    <button
      onClick={handleClick}
      className={clsx(
        'flex items-center justify-center overflow-hidden rounded-md ring-2 transition-all hover:ring-black',
        {
          'ring-black ring-offset-1': isSelected,
          'ring-black/20 ring-offset-1 md:ring-black/0': !isSelected,
        }
      )}
      onContextMenu={handleContextMenu}
    >
      <MemoizedFadeImage
        src={img}
        alt=''
        width={48}
        height={48}
        unoptimized
        overlay
        className='aspect-square rounded-md object-cover'
      />
    </button>
  )
})

const ResultImagesCarouselModal: React.FC<Props> = memo(
  function ResultImagesCarouselModal({ images, onClickCallback }) {
    // Stabilize initial focused image
    const [focusedImage, setFocusedImage] = useState<string>()

    useEffect(() => {
      if (images?.[0] && !focusedImage) {
        setFocusedImage(images[0])
      }
    }, [images, focusedImage])

    // Memoize the filtered images array
    const displayImages = useMemo(
      () => (images || []).slice(0, 6).filter(Boolean),
      [images]
    )

    // Memoize the image click handler
    const handleImageClick = useCallback(
      (img: string, openModal?: () => void) => {
        setFocusedImage(img)
        if (openModal) {
          openModal()
        }
      },
      []
    )

    // Memoize the render images function
    const renderImages = useCallback(
      (closeModal?: () => void) => (
        <div className='flex flex-wrap items-center gap-2.5'>
          {displayImages.map((img: string) => (
            <ImageButton
              key={img}
              img={img}
              focusedImage={focusedImage}
              onClick={(img) => handleImageClick(img, closeModal)}
            />
          ))}
        </div>
      ),
      [displayImages, focusedImage, handleImageClick]
    )

    // Memoize the modal content to prevent re-renders
    const modalContent = useCallback(
      ({ closeModal }: { closeModal: () => void }) => (
        <div>
          <div className='max-h-[60vh] overflow-auto md:max-h-[70vh]'>
            {focusedImage && (
              <MemoizedFadeImage
                src={focusedImage}
                alt=''
                width={0}
                height={0}
                sizes='(max-width: 640px) 100vw, 640px'
                unoptimized
                className='h-auto w-full object-cover'
              />
            )}
          </div>
          <div className='space-y-4 px-4 py-6'>
            <div>{renderImages()}</div>
            <Button
              size='lg'
              className='w-full'
              onClick={() => {
                onClickCallback && onClickCallback(focusedImage, closeModal)
              }}
            >
              Use Selected Image
            </Button>
          </div>
        </div>
      ),
      [focusedImage, onClickCallback, renderImages]
    )

    // Memoize the trigger content
    const triggerContent = useCallback(
      ({ openModal }: { openModal: () => void }) => (
        <>{renderImages(openModal)}</>
      ),
      [renderImages]
    )

    if (!displayImages.length) return null

    return (
      <ModalDialog
        bottomSheet
        closeOnSwipeDown
        showCloseButton
        trigger={triggerContent}
      >
        {modalContent}
      </ModalDialog>
    )
  }
)

export default ResultImagesCarouselModal
